<template>
  <div :key="evalDetId">
    <jcdc-headline
      size="h2"
      :title="headingData.evalArea"
      :subtitle="formatSubtitle(headingData)"
    >
      <jcdc-labeled-text :items="formatEvalDetails(headingData)" class="py-2" />
    </jcdc-headline>
    <JcdcRatingsKey
      :items="ratingsData"
      :length="5"
      class="mt-2 mb-7"
      title="Ratings Key"
    />
    <jcdc-data-table
      :headers="headerData"
      :loading="loadingTable"
      :items="tableData"
      disable-sort
      show-all
    >
      <!-- Ratings -->
      <template #[`item.StaffRating`]="{ item }">
        <JcdcRatings
          :value="item.StaffRating"
          readonly
          :disabled="item.StaffRating == ''"
          :length="5"
          class="py-3"
        />
      </template>
      <template #[`item.StudentRating`]="{ item }">
        <JcdcRatings
          :value="item.StudentRating"
          :length="5"
          edit
          :readonly="alumniUser"
          :disabled="alumniUser && item.StudentRating == ''"
          @input="editRatings(item, $event)"
        />
      </template>
      <!-- Footer / Totals -->
      <template #[`body.append`]="{ isMobile }">
        <!-- Desktop Footer -->
        <tr class="tfoot" :class="isMobile ? 'd-flex flex-column' : ''">
          <td v-if="!isMobile"></td>
          <td v-for="item in footerData" :key="item.user" class="py-3">
            <!-- Average / Total -->
            <jcdc-labeled-text
              :items="
                item.user == 'Staff'
                  ? {
                      Staff: {
                        Average: item.average.toFixed(2),
                        Total: item.total,
                      },
                    }
                  : {
                      Student: {
                        Average: item.average.toFixed(2),
                        Total: item.total,
                      },
                    }
              "
            />
            <!-- Comments -->
            <jcdc-labeled-text
              v-if="item.comments"
              class="pt-3"
              :items="{ Comment: item.comments }"
            />
            <span v-if="item.user == 'Student' && !alumniUser">
              <span v-if="!item.comments">Add</span>
              <span v-else>Edit</span>
              Comment
              <jcdc-comment-modal
                class="d-inline-block"
                title="Student Comment"
                :comment="item.comments"
                :edited="item.editedComment"
                @save="editComment($event)"
              />
            </span>
            <jcdc-labeled-text
              v-if="item.lastModDt"
              class="secondary--text text--lighten-1 pt-3"
              :items="{
                'edited by': item.lastModUser,
                'edited on': formatDisplayDate(item.lastModDt),
              }"
            />
          </td>
        </tr>
        <!-- Mobile Footer -->
      </template>
      <!-- Below Table -->
      <template #footer>
        <div v-if="!alumniUser" class="mt-6 d-flex justify-end align-center">
          <div v-if="!currentEvalEdited" class="mr-2">
            Please complete all ratings before saving.
          </div>
          <jcdc-btn
            icon="mdi-content-save"
            align="right"
            icon-right
            :disabled="!currentEvalEdited"
            small
            @click="saveTable()"
            >Save</jcdc-btn
          >
        </div>
      </template>
    </jcdc-data-table>
    <jcdc-flash-confirm ref="evalConfirm" />
  </div>
</template>

<script>
import Vue from 'vue'
import { formatDateTimeValue, formatDisplayDate } from '@/util/formatters'
import { apiClient } from '@/services/'
import { mapActions, mapGetters, mapState } from 'vuex'
import dayjs from 'dayjs'
import {
  JcdcBtn,
  JcdcCommentModal,
  JcdcDataTable,
  JcdcFlashConfirm,
  JcdcHeadline,
  JcdcLabeledText,
  JcdcRatings,
  JcdcRatingsKey,
} from '@/components/jcdc/index'

export default Vue.extend({
  components: {
    JcdcDataTable,
    JcdcRatings,
    JcdcCommentModal,
    JcdcBtn,
    JcdcLabeledText,
    JcdcFlashConfirm,
    JcdcHeadline,
    JcdcRatingsKey,
  },
  beforeRouteUpdate(to, from, next) {
    this.routeGuard(to, from, next)
  },
  beforeRouteLeave(to, from, next) {
    this.routeGuard(to, from, next)
  },
  props: {
    evalDetId: { type: [Number, String], required: true },
  },
  data() {
    return {
      alumniUser: this.$store.state.userInfo.userRole.includes(3),
      headerData: [
        {
          text: this.$vuetify.breakpoint.xsOnly
            ? 'CSS'
            : 'Career Success Standard',
          value: 'Factor',
        },
        {
          text: this.$vuetify.breakpoint.xsOnly ? 'Staff' : 'Staff Rating',
          value: 'StaffRating',
          width: '33%',
        },
        {
          text: this.$vuetify.breakpoint.xsOnly ? 'Student' : 'Student Rating',
          value: 'StudentRating',
          width: '33%',
        },
      ],
      ratingsData: [
        { title: 'Excels', descriptions: [{ rating: 5, text: 'Exceptional' }] },
        {
          title: 'Meets Standards',
          descriptions: [
            { rating: 4, text: 'Above Average' },
            { rating: 3, text: 'Average' },
          ],
        },
        {
          title: 'Needs Improvement',
          descriptions: [
            { rating: 2, text: 'Below Average' },
            { rating: 1, text: 'Unacceptable' },
          ],
        },
      ],
    }
  },
  computed: {
    ...mapGetters('evaluations', ['allEvaluations']),
    ...mapState('evaluations', [
      'evalTableData',
      'loadingTable',
      'footerData',
      'tableData',
      'currentEvaluation',
      'currentEvalEdited',
    ]),
    headingData() {
      const headingData = this.allEvaluations.filter(
        (x) => x.evalDetId == this.evalDetId
      )
      return headingData[0]
    },
    allRatedList() {
      return this.tableData.filter((x) => x.StudentRating > 0)
    },
  },
  watch: {
    $route() {
      this.$store.commit('evaluations/SET_EVAL_EDITED', false)
      this.$store.commit('evaluations/SET_CURRENT_EVAL', this.headingData)
      this.getEvalDetails(this.currentEvaluation)
    },
  },
  methods: {
    formatDateTimeValue,
    formatDisplayDate,
    ...mapActions('evaluations', ['getEvalDetails']),
    saveTable() {
      // Format Data
      const tableEdited = this.evalTableData,
        // Get and map Student Model Updates
        originalStudentModels = this.evalTableData.listOfStudentModels,
        updatedStudentModels = this.tableData.map((item) => {
          return {
            evalFactorId: item.id,
            evalScoreId: item.StudentRating,
            lastModUser: this.footerData[1].lastModUser,
            lastModDt: dayjs(this.footerData[1].lastModDt).format(),
          }
        }),
        studentModels = originalStudentModels.map((old) => ({
          ...old,
          ...updatedStudentModels.find(
            (student) => student.evalFactorId === old.evalFactorId
          ),
        }))
      // Assign new values to object
      Object.assign(tableEdited, {
        listOfStudentModelsAverage: this.footerData[1].average,
        listOfStudentModelsTotal: this.footerData[1].total,
        studComments: this.footerData[1].comments,
        listOfStudentModels: studentModels,
      })
      delete tableEdited.errorMsg
      delete tableEdited.errorType
      apiClient.post('/SaveEvaluationForm', tableEdited).then(() => {
        this.getEvalDetails(this.currentEvaluation)
        this.$store.commit('evaluations/SET_EVAL_EDITED', false)
      })
    },
    // Editing Functions - MN
    editComment(newComment) {
      // Add to footer
      Object.assign(this.footerData[1], {
        comments: newComment,
        editedComment: true,
      })
      this.ratingsComplete()
    },
    editRatings(item, newRating) {
      this.$store.commit('evaluations/SET_EVAL_EDITED', true)
      // Find index of selected item
      const foundIndex = this.tableData.findIndex((x) => x.id == item.id)
      // Add edits to table using index
      Object.assign(this.tableData[foundIndex], { StudentRating: newRating })
      // Monitor Ratings
      const allRatedTotal = this.allRatedList
          .map((a) => a.StudentRating)
          .reduce(function (a, b) {
            return a + b
          }),
        allRatedAverage = allRatedTotal / this.allRatedList.length
      // Assign calculations to footer data
      Object.assign(this.footerData[1], {
        average: allRatedAverage,
        total: allRatedTotal,
      })
      this.ratingsComplete()
    },
    ratingsComplete() {
      // Ratings Complete
      if (this.allRatedList.length == this.tableData.length) {
        this.updateModData()
        this.$store.commit('evaluations/SET_EVAL_EDITED', true)
      } else {
        this.$store.commit('evaluations/SET_EVAL_EDITED', false)
      }
    },
    updateModData() {
      // Add to footer
      Object.assign(this.footerData[1], {
        lastModUser: this.$store.state.adName,
        lastModDt: formatDateTimeValue(new Date()),
      })
    },
    // Heading Data
    formatEvalDetails(item) {
      let evalSchedDt = ''
      if (item.evalSchedDt) {
        if (item.evalSchedDt == '0001-01-01T00:00:00') {
          evalSchedDt = 'N/A'
        } else {
          evalSchedDt = this.formatDisplayDate(item.evalSchedDt)
        }
      }
      return {
        'Scheduled Date': evalSchedDt,
        Evaluator: item.staffName,
        Type: item.evalType,
        Absences: {
          Excused: item.excusedAbsence,
          Unexcused: item.unexcusedAbsence,
        },
        Tardiness: {
          Excused: item.excusedTardy,
          Unexcused: item.unexcusedTardy,
        },
      }
    },
    formatSubtitle(item) {
      if (item.evalArea.includes('Residential'))
        return `Dorm: ${item.evalDetailDesc}`
      return item.evalDetailDesc
    },
    routeGuard(to, from, next) {
      if (this.currentEvalEdited) {
        this.$refs.evalConfirm
          .show({
            message: `Are you sure you want exit this page?
              You have unsaved changes.`,
            okButton: 'Exit Without Saving',
            cancelButton: 'Keep Editing',
          })
          .then((ok) => {
            if (ok) next()
            else next(false)
          })
      } else {
        next()
      }
    },
  },
})
</script>
<style lang="scss" scoped>
.tfoot {
  vertical-align: top;
  td {
    height: inherit !important;
  }
}
</style>
